import { useQuery } from "@tanstack/react-query";
import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";

import {
  storeVehiclesMakesList,
  storeVehiclesModelsList,
  storeVehiclesVariantsList,
  type PickerItem,
} from "@motolynk/api-client";
import { numberProblem, useRules } from "@motolynk/app-core";
import { useTranslation } from "@motolynk/i18n";
import {
  Alert,
  Button,
  EmptyState,
  FormField,
  Icon,
  Input,
  NumberInput,
  Select,
} from "@motolynk/ui";

import type { EditorValues, FitmentValues } from "./values";

const ANY = "__any__";
/** The API's year range for fitment rows (backend apps/products/services/editor.py: 1950 to this year + 2). */
const FIRST_YEAR = 1950;
const lastYear = () => new Date().getFullYear() + 2;
const vehicleKeys = {
  makes: ["vehicles", "picker", "makes"] as const,
  models: (make: string) => ["vehicles", "picker", "models", make] as const,
  variants: (model: string) => ["vehicles", "picker", "variants", model] as const,
};

export function useMakes() {
  return useQuery({
    queryKey: vehicleKeys.makes,
    queryFn: async () => (await storeVehiclesMakesList()).data,
    staleTime: 5 * 60_000,
  });
}

/**
 * Universal fit, or the vehicles (brand → model → variant, year range) this part fits.
 * `typeLocked` hides the universal / specific choice (the bulk editor always edits vehicle rows).
 */
export function FitmentSection({ typeLocked = false }: { typeLocked?: boolean }) {
  const { t } = useTranslation("products");
  const { control, formState } = useFormContext<EditorValues>();
  const { fields, append, remove } = useFieldArray({ control, name: "fitment", keyName: "key" });
  const fitmentType = useWatch({ control, name: "fitment_type" });
  const errors = formState.errors.fitment as
    | (Record<number, Record<string, { message?: string }>> & {
        message?: string;
        root?: { message?: string };
      })
    | undefined;
  const rootError = errors?.message ?? errors?.root?.message;

  return (
    <div className="space-y-4">
      {!typeLocked && (
        <FormField
          label={t("editor.sections.fitment")}
          error={formState.errors.fitment_type?.message}
        >
          <Controller
            control={control}
            name="fitment_type"
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
                options={[
                  { value: "universal", label: t("editor.fitment.universal") },
                  { value: "specific", label: t("editor.fitment.specific") },
                ]}
              />
            )}
          />
        </FormField>
      )}
      {rootError && <Alert tone="danger">{rootError}</Alert>}
      {fitmentType === "specific" && (
        <>
          {fields.length === 0 ? (
            <EmptyState icon="car" title={t("editor.fitment.empty")} />
          ) : (
            <ul className="space-y-3">
              {fields.map((row, index) => (
                <FitmentRow
                  key={row.key}
                  index={index}
                  errors={errors?.[index]}
                  onRemove={() => remove(index)}
                />
              ))}
            </ul>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              append({
                make: "",
                make_name: "",
                model: "",
                model_name: "",
                variant: "",
                variant_name: "",
                year_from: "",
                year_to: "",
                note: "",
              } satisfies FitmentValues)
            }
          >
            <Icon name="plus" size={14} />
            {t("editor.fitment.add")}
          </Button>
        </>
      )}
    </div>
  );
}

function FitmentRow({
  index,
  errors,
  onRemove,
}: {
  index: number;
  errors?: Record<string, { message?: string }>;
  onRemove: () => void;
}) {
  const { t } = useTranslation("products");
  const { control, register, setValue } = useFormContext<EditorValues>();
  const rules = useRules();
  const last = lastYear();
  const yearCheck = (value: string) => {
    const problem = numberProblem(value, { decimals: 0 });
    if (problem) return rules.message(problem) ?? true;
    const year = Number(value);
    if (value.trim() && (year < FIRST_YEAR || year > last))
      return rules.message({ key: "yearRange", values: { min: FIRST_YEAR, max: last } }) ?? true;
    return true;
  };
  const row = useWatch({ control, name: `fitment.${index}` });
  const makes = useMakes();
  const make = (makes.data ?? []).find((item) => item.uid === row?.make);
  const models = useQuery({
    queryKey: vehicleKeys.models(make?.slug ?? ""),
    queryFn: async () => (await storeVehiclesModelsList({ make: make?.slug ?? "" })).data,
    enabled: Boolean(make?.slug),
    staleTime: 5 * 60_000,
  });
  const variants = useQuery({
    queryKey: vehicleKeys.variants(row?.model ?? ""),
    queryFn: async () => (await storeVehiclesVariantsList({ model: row?.model ?? "" })).data,
    enabled: Boolean(row?.model),
    staleTime: 5 * 60_000,
  });
  const options = (items: PickerItem[] | undefined, current: string, currentName: string) => {
    const list = (items ?? []).map((item) => ({ value: item.uid, label: item.name }));
    // keep showing a saved choice whose list hasn't loaded (or that was switched off since)
    if (current && !list.some((item) => item.value === current))
      list.unshift({ value: current, label: currentName || "…" });
    return list;
  };
  const set = (field: keyof FitmentValues, value: string) =>
    setValue(`fitment.${index}.${field}`, value, { shouldDirty: true });

  return (
    <li className="grid gap-3 rounded-card border border-border p-3 sm:grid-cols-6">
      <FormField
        label={t("editor.fitment.make")}
        error={errors?.make?.message}
        className="sm:col-span-2"
        required
      >
        <Select
          value={row?.make || ANY}
          placeholder={t("editor.fitment.chooseMake")}
          onValueChange={(value) => {
            const chosen = (makes.data ?? []).find((item) => item.uid === value);
            set("make", value === ANY ? "" : value);
            set("make_name", chosen?.name ?? "");
            set("model", "");
            set("model_name", "");
            set("variant", "");
            set("variant_name", "");
          }}
          options={[
            { value: ANY, label: t("editor.fitment.chooseMake") },
            ...options(makes.data, row?.make ?? "", row?.make_name ?? ""),
          ]}
        />
      </FormField>
      <FormField
        label={t("editor.fitment.model")}
        error={errors?.model?.message}
        className="sm:col-span-2"
      >
        <Select
          value={row?.model || ANY}
          disabled={!row?.make}
          onValueChange={(value) => {
            const chosen = (models.data ?? []).find((item) => item.uid === value);
            set("model", value === ANY ? "" : value);
            set("model_name", chosen?.name ?? "");
            set("variant", "");
            set("variant_name", "");
          }}
          options={[
            { value: ANY, label: t("editor.fitment.anyModel") },
            ...options(models.data, row?.model ?? "", row?.model_name ?? ""),
          ]}
        />
      </FormField>
      <FormField
        label={t("editor.fitment.variant")}
        error={errors?.variant?.message}
        className="sm:col-span-2"
      >
        <Select
          value={row?.variant || ANY}
          disabled={!row?.model}
          onValueChange={(value) => {
            const chosen = (variants.data ?? []).find((item) => item.uid === value);
            set("variant", value === ANY ? "" : value);
            set("variant_name", chosen?.name ?? "");
          }}
          options={[
            { value: ANY, label: t("editor.fitment.anyVariant") },
            ...options(
              variants.data as PickerItem[] | undefined,
              row?.variant ?? "",
              row?.variant_name ?? "",
            ),
          ]}
        />
      </FormField>
      <FormField label={t("editor.fitment.yearFrom")} error={errors?.year_from?.message}>
        <NumberInput
          {...register(`fitment.${index}.year_from`, { validate: yearCheck })}
          min={FIRST_YEAR}
          max={last}
          aria-invalid={Boolean(errors?.year_from)}
        />
      </FormField>
      <FormField label={t("editor.fitment.yearTo")} error={errors?.year_to?.message}>
        <NumberInput
          {...register(`fitment.${index}.year_to`, {
            validate: (value, all) => {
              const problem = yearCheck(value);
              if (problem !== true) return problem;
              const from = all.fitment[index]?.year_from ?? "";
              if (value.trim() && from.trim() && Number(value) < Number(from))
                return rules.message({ key: "toBeforeFrom" }) ?? true;
              return true;
            },
          })}
          min={FIRST_YEAR}
          max={last}
          aria-invalid={Boolean(errors?.year_to)}
        />
      </FormField>
      <FormField
        label={t("editor.fitment.note")}
        error={errors?.note?.message}
        className="sm:col-span-3"
      >
        <Input {...register(`fitment.${index}.note`)} />
      </FormField>
      <div className="flex items-end justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`${t("editor.fitment.remove")} ${index + 1}`}
          onClick={onRemove}
        >
          <Icon name="trash" size={16} />
        </Button>
      </div>
    </li>
  );
}
